import { useEffect, useMemo, useState } from 'react';

import { AddManualLotEnabled } from 'common/utils/featureFlags';

import useManualLotDraft from './useManualLotDraft';
import { BULK_APPLY_SCOPE } from '../utils/bulkApplyPlan';
import { buildStockLotSelection } from '../utils/bulkLotNdcStaging';
import { toDateOnly } from '../utils/bulkLotNdcStatus';
import {
  buildBulkLotUpdateAnchor,
  canRecordBulkLotByHand,
  findBusiestFacility,
  findMergedBulkLots,
} from '../utils/bulkLotUpdateChoices';
import { describeBulkLotUpdate, toBulkChanges } from '../utils/bulkLotUpdateOutcome';

const NO_UNTICKED_ROWS = [];

const defaultScopeFor = (choice) =>
  choice?.needsAttentionCount > 0 ? BULK_APPLY_SCOPE.NEEDS_ATTENTION : BULK_APPLY_SCOPE.ALL_ROWS;

/**
 * POM-6537 — Bulk Lot Update's state: the NDC, the lot (from stock, or recorded by hand at one
 * facility), which rows, what Apply would do to them, and whether the operator confirmed it.
 *
 * A stock lot is one lot number and BUD across the NDC's facilities (findMergedBulkLots), so each
 * row takes its own facility's record of it. A lot recorded by hand is one vial at one facility,
 * so it alone asks for a facility. Everything is planned by the review's planner (bulkApplyPlan),
 * which also plans "Also apply", so a lot reaches the same rows whichever way it is applied.
 *
 * The confirmation is of the change the preview states: the lot, the rows and the System Deficit.
 * Any change to one of them, from any control, asks for it again, even on coming back.
 */
const useBulkLotUpdate = ({
  choices,
  initialChoiceKey,
  availabilityFor,
  recordableFor,
  batchLotContextFor,
  batchLotShareFor,
  planBulkApplyFor,
  lotLookupCache,
  todayStr,
}) => {
  const [selectedKey, setSelectedKey] = useState(initialChoiceKey);
  const [selectedLotKey, setSelectedLotKey] = useState(null);
  const [manualFacilityId, setManualFacilityId] = useState(null);
  const [scope, setScope] = useState(() =>
    defaultScopeFor(choices.find((candidate) => candidate.key === initialChoiceKey))
  );
  const [confirmedChange, setConfirmedChange] = useState(null);

  const choice = choices.find((candidate) => candidate.key === selectedKey) ?? null;
  const lots = useMemo(
    () =>
      findMergedBulkLots({
        choice,
        availabilityFor,
        batchLotShareFor,
        todayStr,
        isManualLotEnabled: AddManualLotEnabled,
      }),
    [choice, availabilityFor, batchLotShareFor, todayStr]
  );

  const manualFacilities = useMemo(
    () =>
      (choice?.facilities ?? []).filter((facility) => {
        const anchor = buildBulkLotUpdateAnchor({ choice, facility });

        return canRecordBulkLotByHand({
          anchor,
          recordable: recordableFor(anchor),
          isManualLotEnabled: AddManualLotEnabled,
        });
      }),
    [choice, recordableFor]
  );
  const manualFacility =
    manualFacilities.find((facility) => facility.fulfillingPharmacyId === manualFacilityId) ??
    findBusiestFacility({ facilities: manualFacilities });
  const manualAnchor = useMemo(
    () => (manualFacility ? buildBulkLotUpdateAnchor({ choice, facility: manualFacility }) : null),
    [choice, manualFacility]
  );
  const manualGroups = useMemo(
    () => (manualAnchor ? (availabilityFor(manualAnchor)?.groups ?? []) : []),
    [manualAnchor, availabilityFor]
  );
  const batchContext = useMemo(
    () => batchLotContextFor(manualAnchor),
    [batchLotContextFor, manualAnchor]
  );
  const manualLotDraft = useManualLotDraft({
    open: true,
    resetKey: choice?.key ?? null,
    recordable: manualAnchor ? recordableFor(manualAnchor) : null,
    sessionLots: batchContext.sessionManualLots,
    groups: manualGroups,
    ndc: choice?.ndc ?? null,
    batchNdcClaims: batchContext.ndcClaims,
    lotLookupCache,
    fulfillingPharmacyId: manualFacility?.fulfillingPharmacyId,
    todayStr,
  });

  const mergedLot = lots.find((lot) => lot.key === selectedLotKey) ?? null;
  let pick = null;
  if (manualLotDraft.isActive) {
    const handLot = manualLotDraft.selectionToApply;
    pick = handLot && {
      anchor: manualAnchor,
      selection: { ndc: handLot.ndc, lotAllocations: [handLot] },
      lot: {
        lotNumber: handLot.lotNumber,
        expirationDate: handLot.expirationDate,
        isManual: true,
        facilityId: manualFacility.fulfillingPharmacyId,
        facilityName: manualFacility.facilityName,
      },
    };
  } else if (mergedLot) {
    const { facility, lot: stockLot } = mergedLot.pickedAt;
    pick = {
      anchor: buildBulkLotUpdateAnchor({ choice, facility }),
      // No quantity of its own: each row's allocation is built for its own fill by the plan.
      selection: {
        ndc: choice.ndc,
        lotAllocations: [buildStockLotSelection({ lot: stockLot, fillQuantity: 0 })],
        stockLot,
      },
      lot: {
        lotNumber: mergedLot.lotNumber,
        expirationDate: mergedLot.expirationDate,
        isManual: false,
        stockedFacilityIds: mergedLot.stockedFacilityIds,
      },
    };
  }

  const entries = pick
    ? (planBulkApplyFor({
        item: pick.anchor,
        selectedNdc: choice.ndc,
        selection: pick.selection,
        scope,
        untickedIds: NO_UNTICKED_ROWS,
      })?.entries ?? [])
    : [];
  const outcome = pick
    ? describeBulkLotUpdate({ ndcRows: choice.ndcRows, entries, pick: pick.lot, scope })
    : null;
  const bulkChanges = toBulkChanges(entries);
  const changeSummary = pick
    ? [
        pick.lot.lotNumber,
        toDateOnly(pick.lot.expirationDate),
        outcome.deficitQty,
        ...bulkChanges.map((change) => change.requestLineItemId),
      ].join('|')
    : null;

  // Dropped as soon as the change moves away, so coming back to it asks again.
  useEffect(() => {
    if (confirmedChange && confirmedChange !== changeSummary) setConfirmedChange(null);
  }, [confirmedChange, changeSummary]);

  return {
    choice,
    lots,
    selectedLotKey,
    manualLotDraft,
    manualFacilities,
    manualFacility,
    pickedLot: pick?.lot ?? null,
    scope,
    // Asked only when the answers differ: some rows need attention and some do not.
    hasScopeChoice:
      !!choice && choice.needsAttentionCount > 0 && choice.needsAttentionCount < choice.rowCount,
    outcome,
    bulkChanges,
    isConfirmed: bulkChanges.length > 0 && confirmedChange === changeSummary,
    selectChoice: (key) => {
      if (key === selectedKey) return;
      setSelectedKey(key);
      setSelectedLotKey(null);
      setManualFacilityId(null);
      setScope(defaultScopeFor(choices.find((candidate) => candidate.key === key)));
    },
    // A fill is from stock or recorded by hand, never both: choosing one drops the other.
    selectStockLot: (lotKey) => {
      setSelectedLotKey(lotKey);
      manualLotDraft.remove();
    },
    startManualEntry: () => {
      setSelectedLotKey(null);
      manualLotDraft.start();
    },
    // The panel stays open on another facility, its draft started afresh: a lot checked at one
    // facility is not checked at the next.
    selectManualFacility: (fulfillingPharmacyId) => {
      setManualFacilityId(fulfillingPharmacyId);
      manualLotDraft.start();
    },
    setScope,
    setConfirmed: (isConfirmed) => setConfirmedChange(isConfirmed ? changeSummary : null),
  };
};

export default useBulkLotUpdate;
